import { observer, useLocalObservable } from 'mobx-react';
import React, { FunctionComponent, ReactNode } from 'react';
import { FormComponentProps } from 'antd/lib/form';
import { RowSeparator } from '@app/components/Styles';
import { Form } from 'antd';
import styled from '@emotion/styled';
import Button from '@app/components/Button';
import { useTimeModel, createTimeModel } from 'react-compound-timer';
import config from '@app/config';
import { FormFieldDecorators } from '@app/utils/form/form';
import useInfraStores from '@app/hooks/useInfraStores';
import { GuestMode } from '@app/guestLogin/stores/GuestInfraStores';
import { LoginFooterSubTitle, LoginSubTitle, LoginTitle } from '../GuestLoginFlow';
import * as messageLauncher from '@app/utils/messageLauncher';
import { extractLogErrorIdFromError } from '@app/libs/request';
import { getCodeEntryErrorKey } from '@app/guestLogin/domain/guestLoginErrors';
import FormItemBox from '@app/components/inputs/FormItemBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import useInnerPageEnterAnalyticsReport, { InnerPage } from '@app/hooks/useInnerPageEnterAnalyticsReport';
import useMountEffect from '@app/hooks/useMountEffect';
import { useTranslation } from 'react-i18next';
import { createTranslatedFormalErrorMessage } from '@app/utils/errorMessageUtils';
import { useGuestLoginI18nNamespace } from '@app/guestLogin/domain/guestLogin';

const timer = createTimeModel({
  initialTime: config.resendLoginCodeTimerDurationSec * 1000,
  direction: 'backward',
  timeToUpdate: 300,
});

interface EnterCodeFormFields {
  code: string;
}

interface Props extends FormComponentProps<EnterCodeFormFields> {
  cancelWaitingForCode: () => void;
  email: string;
}

const GuestLoginEnterEmailCode: FunctionComponent<Props> = observer((props) => {
  useInnerPageEnterAnalyticsReport(InnerPage.loginEnterEmailCode);
  const { guestLoginStore } = useInfraStores<GuestMode>();
  const { t } = useTranslation();
  const ns = useGuestLoginI18nNamespace();

  const codeLength = guestLoginStore.emailKnoxer?.codeLength ?? 0;

  const localStore = useLocalObservable(() => ({
    inputErrorText: null as string | null,

    setInputErrorText: (errorText: string | null): void => {
      localStore.inputErrorText = errorText;
    },
  }));

  const verifySentCode = async (code: string): Promise<void> => {
    try {
      await guestLoginStore.requestTokenFromEmailKnoxer(code);
    } catch (e: unknown) {
      const inlineErrorKey = getCodeEntryErrorKey(e);

      if (inlineErrorKey) {
        localStore.setInputErrorText(t(`${ns}.errors.${inlineErrorKey}`));
      } else {
        const errorLogId = extractLogErrorIdFromError(e);
        const errorMessage = createTranslatedFormalErrorMessage(
          t,
          `${ns}.errors.unexpectedLoginError`,
          'general.errors.pleaseContact',
          'general.errors.referToErrorCode',
          errorLogId,
        );

        messageLauncher.shootErrorOld(errorMessage, t('general.errors.errorPopupTitle'));
      }
    }
  };

  // Validated on blur so the length rule doesn't nag mid-entry. Emptiness is deliberately not a
  // rule: focus also leaves the untouched field when the supplier switches to their mailbox to
  // read the code, and that must not surface an error -- see handleOk.
  const fieldDecorators: FormFieldDecorators<EnterCodeFormFields> = {
    code: {
      validateTrigger: 'onBlur',
      rules: [
        {
          len: codeLength,
          translatedMessage: {
            key: `${ns}.errors.codeLength`,
            value: codeLength,
          },
        },
        {
          pattern: /^[0-9]+$/,
          translatedMessage: `${ns}.errors.mustBeNumeric`,
        },
      ],
    },
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await handleOk();
  };

  const handleOk = (): void => {
    const { form } = props;
    const { validateFieldsAndScroll } = form;

    // The input submits on its last digit and the form on Enter, so a second submit while an
    // exchange is in flight would only re-send the same code and have it rejected as already used.
    if (guestLoginStore.isLoginLoading) {
      return;
    }

    validateFieldsAndScroll((errors: Object, values: EnterCodeFormFields) => {
      if (errors) {
        return;
      }

      const { code } = values;

      if (!code) {
        localStore.setInputErrorText(t(`${ns}.errors.enterCode`));
        return;
      }

      verifySentCode(code);
    });
  };

  const handleCodeInputClick = (): void => {
    const { resetFields } = props.form;

    if (localStore.inputErrorText) {
      resetFields();
      localStore.setInputErrorText(null);
    }
  };

  const onBack = (): void => {
    props.cancelWaitingForCode();
  };

  const { value: timeModel, start: startResendTimer, stop: stopResendTimer, reset: resetResendTimer } = useTimeModel(timer);

  // The screen only mounts right after a code was sent, so restart the cooldown fresh each time
  // instead of letting it run from whenever this module first loaded.
  useMountEffect(() => {
    stopResendTimer();
    resetResendTimer();
    startResendTimer();
  });

  const isResendCoolingDown = timeModel.state === 'PLAYING' || timeModel.state === 'INITED';

  const formatValue = (value: number): string => `${value < 10 ? `0${value}` : value}`;

  const onResendCode = async (): Promise<void> => {
    const didRequestSucceed = await guestLoginStore.requestCodeFromEmailKnoxer(email);

    if (didRequestSucceed) {
      resetResendTimer();
      startResendTimer();
    }
  };

  const renderResendButton = (): ReactNode => (
    <Button
      id='btn-guest-email-enter-code-resend'
      appearance='text'
      type='button'
      size='small'
      disabled={isResendCoolingDown}
      aria-label={t(`${ns}.emailPassword.resend`)}
      onClick={onResendCode}
    >
      {isResendCoolingDown ? `${formatValue(timeModel.m)}:${formatValue(timeModel.s)}` : t(`${ns}.emailPassword.resend`)}
    </Button>
  );

  const { form, email } = props;

  return (
    <>
      <ContentContainer>
        <LoginTitle>{t(`${ns}.emailPassword.emailVerification`)}</LoginTitle>
        <LoginSubTitle>{t(`${ns}.emailPassword.checkForCode`, { email })}</LoginSubTitle>
        <EnterCodeForm onSubmit={handleFormSubmit}>
          <FormItemBox
            form={form}
            fieldName='code'
            appearance='corners'
            fieldDecoratorOptions={fieldDecorators.code}
            forcedErrorText={localStore.inputErrorText}
          >
            <NakedFormInput
              name='inpt-guest-email-enter-code'
              type='number'
              autoFocus
              placeholder={t<string>(`${ns}.emailPassword.enterCode`, { codeLength })}
              onMaxLengthAchieved={handleOk}
              maxLength={codeLength}
              onClick={handleCodeInputClick}
              onFocus={handleCodeInputClick}
            />
          </FormItemBox>
          <ActionsContainer>
            <Button id='btn-guest-email-enter-code-back' appearance='text' type='button' onClick={onBack} size='small'>
              {t('general.upperBack')}
            </Button>
            {renderResendButton()}
          </ActionsContainer>
        </EnterCodeForm>
      </ContentContainer>
      <RowSeparator />
      <FooterContainer>
        <BoldSubtitle>{t(`${ns}.emailPassword.notArriving`)}</BoldSubtitle>
        <LoginFooterSubTitle>{t(`${ns}.emailPassword.checkSpam`, { email })}</LoginFooterSubTitle>
      </FooterContainer>
    </>
  );
});

export default Form.create<Props>()(GuestLoginEnterEmailCode);

const ContentContainer = styled.div`
  padding: 32px 64px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const FooterContainer = styled.div`
  padding: 16px 72px 32px;
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

const EnterCodeForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  padding-right: 5px;
  padding-left: 5px;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 12px;
`;

const BoldSubtitle = styled.span`
  font-family: var(--text-font-family);
  font-size: 16px;
  font-weight: bold;
  line-height: 1.25;

  color: var(--transparent-black-900);

  margin-bottom: 8px;
`;
